import React, { useState, useEffect, useCallback } from 'react';
import {
    ArrowLeft,
    ChevronRight,
    CheckCircle,
    XCircle,
    Loader2,
    BookOpen
} from 'lucide-react';
import { getAuth } from 'firebase/auth';
import CitationDisplay from './CitationDisplay';

const API_URL = process.env.REACT_APP_API_URL;

const ReviewQuestionsPage = ({ onBack, category, filter, questionUuid }) => {
    const [reviewQuestions, setReviewQuestions] = useState([]); // [{question_uuid, user_selection}]
    const [currentIndex, setCurrentIndex] = useState(0);
    const [questionData, setQuestionData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const auth = getAuth();
    const user = auth.currentUser;

    const isSingleQuestionMode = !!questionUuid;
    const isCorrectReview = filter === 'correct';

    // Determine labels based on mode
    let categoryLabel, filterLabel;

    if (isSingleQuestionMode) {
        categoryLabel = 'Shared Question';
        filterLabel = 'Preview';
    } else {
        categoryLabel = category === 'ostomy' ? 'Ostomy' : 'Wound Care';
        filterLabel = isCorrectReview ? 'Correct' : 'Incorrect';
    }

    // Fetch question UUIDs and user selections
    useEffect(() => {
        const fetchQuestionUuids = async () => {
            // If in single question mode, we don't need to fetch a list
            if (questionUuid) {
                setReviewQuestions([{
                    question_uuid: questionUuid,
                    question_version: null, // Default to latest/random
                    user_selection: null
                }]);
                setLoading(false);
                return;
            }

            if (!user) {
                setLoading(false);
                return;
            }

            try {
                const token = await user.getIdToken(true);
                const response = await fetch(
                    `${API_URL}/api/account/review-questions?category=${category}&filter=${filter}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    }
                );

                if (!response.ok) {
                    throw new Error('Failed to fetch questions');
                }

                const data = await response.json();
                setReviewQuestions(data.questions || []);

                if ((data.questions || []).length === 0) {
                    setLoading(false);
                }
            } catch (err) {
                console.error('Error fetching question UUIDs:', err);
                setError(err.message);
                setLoading(false);
            }
        };

        fetchQuestionUuids();
    }, [user, category, filter, questionUuid]);

    // Fetch current question data by UUID and version
    const fetchQuestion = useCallback(async (questionUuid, questionVersion) => {
        if (!user || !questionUuid) return;

        setLoading(true);
        try {
            const token = await user.getIdToken(true);
            // Pass version param to get the exact version that was answered
            const versionParam = questionVersion ? `?version=${questionVersion}` : '';
            const response = await fetch(`${API_URL}/questions/${questionUuid}${versionParam}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch question');
            }

            const data = await response.json();

            // Transform to expected format (now uses uuid instead of id)
            const options = [
                data.choice_a,
                data.choice_b,
                data.choice_c,
                data.choice_d
            ].filter(o => o !== null);

            const answerMap = { 'a': 0, 'b': 1, 'c': 2, 'd': 3 };
            const correctAnswerIndex = answerMap[data.answer.toLowerCase()];

            setQuestionData({
                uuid: data.uuid,
                question: data.question,
                options,
                correctAnswer: correctAnswerIndex,
                explanation: data.explanation,
                heading: data.heading,
                citation: data.citations?.[0] || null
            });
        } catch (err) {
            console.error('Error fetching question:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [user]);

    // Load first question when UUIDs are available
    useEffect(() => {
        if (reviewQuestions.length > 0 && currentIndex < reviewQuestions.length) {
            const currentQ = reviewQuestions[currentIndex];
            fetchQuestion(currentQ.question_uuid, currentQ.question_version);
        }
    }, [reviewQuestions, currentIndex, fetchQuestion]);

    const handleNext = () => {
        if (currentIndex < reviewQuestions.length - 1) {
            setQuestionData(null); // Clear to prevent flash of old data
            setCurrentIndex(prev => prev + 1);
        }
    };

    const handlePrevious = () => {
        if (currentIndex > 0) {
            setQuestionData(null); // Clear to prevent flash of old data
            setCurrentIndex(prev => prev - 1);
        }
    };

    // Get current user's selection (convert 'a','b','c','d' to index 0,1,2,3)
    const getCurrentUserSelection = () => {
        if (reviewQuestions.length === 0 || currentIndex >= reviewQuestions.length) return null;
        const selection = reviewQuestions[currentIndex].user_selection?.toLowerCase();
        if (!selection) return null;
        const selectionMap = { 'a': 0, 'b': 1, 'c': 2, 'd': 3 };
        return selectionMap[selection] ?? null;
    };

    // Loading state
    if (loading && !questionData) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 flex flex-col items-center justify-center">
                <Loader2 className="w-16 h-16 animate-spin mb-4 text-slate-500" />
                <p className="text-2xl font-semibold text-gray-700">Loading questions...</p>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-red-500 to-orange-500 p-4 flex flex-col items-center justify-center text-white text-center">
                <XCircle className="w-16 h-16 mb-4" />
                <p className="text-2xl font-semibold mb-2">Error loading questions</p>
                <p className="mb-4">{error}</p>
                <button
                    onClick={onBack}
                    className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold bg-white text-red-600 hover:shadow-lg transition-all"
                >
                    <ArrowLeft className="w-5 h-5" /> Back to Results
                </button>
            </div>
        );
    }

    // Empty state
    if (reviewQuestions.length === 0) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 flex flex-col items-center justify-center text-center">
                <BookOpen className="w-16 h-16 mb-4 text-gray-300" />
                <p className="text-2xl font-semibold mb-2 text-gray-700">No questions to review</p>
                <p className="mb-4 text-gray-500">
                    You don't have any {filterLabel.toLowerCase()} answers in {categoryLabel} yet.
                </p>
                <button
                    onClick={onBack}
                    className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold bg-white shadow-md text-gray-700 hover:shadow-lg transition-all"
                >
                    <ArrowLeft className="w-5 h-5" /> Back to Results
                </button>
            </div>
        );
    }

    const userSelectionIndex = getCurrentUserSelection();

    return (
        <div className={`min-h-screen p-4 flex items-center justify-center ${isSingleQuestionMode
                ? 'bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500'
                : (isCorrectReview
                    ? 'bg-gradient-to-br from-green-500 via-emerald-500 to-teal-500'
                    : 'bg-gradient-to-br from-red-500 via-rose-500 to-pink-500')
            }`}>
            {/* Back button */}
            <button
                onClick={onBack}
                className="fixed top-4 left-4 md:top-6 md:left-6 z-50 flex items-center gap-2 px-4 py-2 md:px-5 md:py-3 bg-white/95 backdrop-blur-sm rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all font-semibold text-gray-700 text-sm md:text-base"
            >
                <ArrowLeft className="w-4 h-4 md:w-5 md:h-5" />
                Back
            </button>

            <div className="w-full max-w-2xl">
                {/* Header */}
                <div className="text-center mb-6">
                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-3 ${isSingleQuestionMode
                        ? 'bg-blue-100 text-blue-800'
                        : (isCorrectReview ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800')
                        }`}>
                        {isSingleQuestionMode
                            ? <BookOpen className="w-5 h-5" />
                            : (isCorrectReview
                                ? <CheckCircle className="w-5 h-5" />
                                : <XCircle className="w-5 h-5" />
                            )
                        }
                        <span className="font-semibold">{categoryLabel} - {filterLabel}</span>
                    </div>
                    <p className="text-white/80 text-sm">
                        Question {currentIndex + 1} of {reviewQuestions.length}
                    </p>
                </div>

                {/* Question Card */}
                <div className="bg-white/95 rounded-3xl shadow-2xl overflow-hidden">
                    {/* Question */}
                    <div className="p-6 md:p-8">
                        <p className="text-lg md:text-xl text-gray-800 font-medium leading-relaxed">
                            {questionData?.question}
                        </p>
                    </div>

                    {/* Options */}
                    <div className="px-6 md:px-8 pb-4 space-y-3">
                        {questionData?.options.map((option, index) => {
                            const isCorrect = index === questionData.correctAnswer;
                            const isUserSelection = index === userSelectionIndex;
                            const isWrongSelection = isUserSelection && !isCorrect;

                            // Determine styling based on correct answer and user selection
                            let borderBgClass = 'border-gray-200 bg-gray-50';
                            if (isCorrect) {
                                borderBgClass = 'border-green-500 bg-green-50';
                            } else if (isWrongSelection) {
                                borderBgClass = 'border-red-500 bg-red-50';
                            }

                            let circleBgClass = 'border-gray-300';
                            if (isCorrect) {
                                circleBgClass = 'border-green-500 bg-green-500';
                            } else if (isWrongSelection) {
                                circleBgClass = 'border-red-500 bg-red-500';
                            }

                            let textClass = 'text-gray-700';
                            if (isCorrect) {
                                textClass = 'font-semibold text-green-700';
                            } else if (isWrongSelection) {
                                textClass = 'font-semibold text-red-700';
                            }

                            return (
                                <div
                                    key={index}
                                    className={`w-full text-left p-4 rounded-xl border-2 ${borderBgClass}`}
                                >
                                    <div className="flex items-center gap-4">
                                        <div className={`w-8 h-8 min-w-[2rem] aspect-square rounded-full border-2 flex items-center justify-center flex-shrink-0 ${circleBgClass}`}>
                                            {isCorrect && <CheckCircle className="w-5 h-5 text-white" />}
                                            {isWrongSelection && <XCircle className="w-5 h-5 text-white" />}
                                        </div>
                                        <div className="flex-1">
                                            <span className={`text-base ${textClass}`}>
                                                {option}
                                            </span>
                                            {isWrongSelection && (
                                                <span className="ml-2 text-xs text-red-600 font-medium">(Your answer)</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Explanation */}
                    <div className="px-6 md:px-8 pb-6">
                        <div className="p-4 rounded-xl bg-blue-50 border-2 border-blue-200">
                            <h3 className="text-sm font-semibold text-blue-800 mb-2">Explanation</h3>
                            <p className="text-gray-700 leading-relaxed text-sm">
                                {questionData?.explanation}
                            </p>

                            {/* Heading/Citation */}
                            <CitationDisplay
                                heading={questionData?.heading}
                                citation={questionData?.citation}
                            />
                        </div>
                    </div>

                    {/* Navigation */}
                    <div className="px-6 md:px-8 pb-6 flex justify-between items-center">
                        <button
                            onClick={handlePrevious}
                            disabled={currentIndex === 0}
                            className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold transition-all ${currentIndex === 0
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                        >
                            <ArrowLeft className="w-4 h-4" /> Previous
                        </button>

                        {currentIndex < reviewQuestions.length - 1 ? (
                            <button
                                onClick={handleNext}
                                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white hover:shadow-lg transition-all ${isCorrectReview
                                    ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                                    : 'bg-gradient-to-r from-red-500 to-rose-500'
                                    }`}
                            >
                                Next <ChevronRight className="w-5 h-5" />
                            </button>
                        ) : (
                            <button
                                onClick={onBack}
                                className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold bg-gradient-to-r from-indigo-500 to-purple-500 text-white hover:shadow-lg transition-all"
                            >
                                Done <CheckCircle className="w-5 h-5" />
                            </button>
                        )}
                    </div>
                </div>

                {/* Progress indicator */}
                <div className="flex justify-center gap-1 mt-6 flex-wrap">
                    {reviewQuestions.map((_, index) => (
                        <div
                            key={index}
                            className={`w-2 h-2 rounded-full transition-all ${index === currentIndex
                                ? 'bg-white scale-125'
                                : index < currentIndex
                                    ? 'bg-white/60'
                                    : 'bg-white/30'
                                }`}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default ReviewQuestionsPage;
